import { useState, useRef, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, TextButton, Popover, generateId, useMediaQuery } from 'bonfire-kindling';
import { useDeleteSearchProfile, useOpportunitiesActiveTab } from 'helpers/opportunities';
import { ConfirmationModal } from 'components';
import { useFeatureFlags } from 'helpers/hooks';

import { useOpportunities } from '../../OpportunitiesProvider';
import SavedSearchList from './SavedSearchList';
import SaveCurrentSearchButton from './SaveCurrentSearchButton';

const OpportunitiesSavedSearch = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const { onDeleteSearchProfile } = useOpportunities();
  const { isFlagEnabled } = useFeatureFlags();
  const { tab } = useOpportunitiesActiveTab();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const anchorId = useMemo(() => generateId(), []);
  const popoverId = useMemo(() => generateId(), []);

  const [profileToDelete, setProfileToDelete] = useState(null);

  const {
    mutation: { mutate },
  } = useDeleteSearchProfile();

  const deleteSavedSearch = useCallback(() => {
    if (profileToDelete) {
      mutate(profileToDelete.id, {
        onSuccess: () => {
          onDeleteSearchProfile(profileToDelete.id);
        },
      });
      setProfileToDelete(null);
    }
  }, [mutate, onDeleteSearchProfile, profileToDelete]);
  const closeDeleteModal = useCallback(() => setProfileToDelete(null), []);

  if (!isFlagEnabled('FF_25_RELEASE_TOGGLE_PV_SAVED_SEARCH')) {
    return null;
  }

  const btns = [
    <TextButton
      key="openSavedSearch"
      id={anchorId}
      variant="outlined"
      label={t('opportunities.filters.saved_search')}
      iconRight="caret-down"
      ref={anchorRef}
      onClick={() => setOpen(true)}
      aria-expanded={open}
      aria-controls={popoverId}
      aria-haspopup="dialog"
      fullWidth={isMobile}
      sx={{ mr: { xs: 0, sm: 3 } }}
    />,
    tab === 'all' || tab === 'saved' ? (
      <SaveCurrentSearchButton key="saveCurrentSearch" tab={tab} />
    ) : null,
  ];

  if (isMobile) {
    btns.reverse();
  }

  return (
    <>
      <Box
        sx={{
          ml: { xs: 0, md: 3 },
          mt: { xs: 3, md: 0 },
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'flex-start', sm: 'flex-end', md: 'flex-start' },
          flex: '1 1 0%',
        }}
      >
        {btns}
      </Box>
      <Popover
        id={popoverId}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        elevation={4}
        onClose={() => setOpen(false)}
        open={open}
        role="dialog"
        aria-labelledby={anchorId}
        PaperProps={{ sx: { width: '20rem' } }}
      >
        <Box sx={{ p: 3 }}>
          <TextButton
            component={RouterLink}
            variant="contained"
            label={t('opportunities.filters.add_advanced_search')}
            to="/settings/saved/new"
            fullWidth
          />
        </Box>
        <SavedSearchList onDelete={setProfileToDelete} />
      </Popover>
      <ConfirmationModal
        open={!!profileToDelete}
        title={t('settings.saved_search.modal.confirm_remove')}
        onClose={closeDeleteModal}
        CancelButtonProps={{ label: t('common.cancel'), onClick: closeDeleteModal }}
        ConfirmButtonProps={{ label: t('common.remove'), onClick: deleteSavedSearch }}
      />
    </>
  );
};

export default OpportunitiesSavedSearch;
